<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <v-card>
      <v-card-title
        style="background: linear-gradient(45deg, #28a745, #20c997); color: white; border-radius: 15px 15px 0 0;"
      >
        <span class="headline">
          <i class="fal fa-file-pdf mr-2"></i>Upload Offer Letters
        </span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <div class="mb-4">
            <p class="text-muted">
              You can upload offer letters for the selected candidates. This is
              optional - you can skip and upload later.
            </p>
          </div>
          <div
            class="table-responsive"
            style="max-height: 400px; overflow-y: auto;"
          >
            <table class="table">
              <thead>
                <tr>
                  <th class="text-left" style="border-top: none;">
                    Candidate Name
                  </th>
                  <th class="text-left" style="border-top: none;">Email</th>
                  <th class="text-left" style="border-top: none;">
                    Offer Letter
                  </th>
                  <th class="text-center" style="border-top: none;">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="candidate in candidates" :key="candidate.id">
                  <td class="text-left">
                    {{ candidate.firstname }} {{ candidate.lastname }}
                  </td>
                  <td class="text-left">{{ candidate.email }}</td>
                  <td class="text-left">
                    <v-file-input
                      :ref="`fileInput_${candidate.id}`"
                      v-model="offerLetters[candidate.id]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-file-pdf-box"
                      label="Select offer letter"
                      accept=".pdf,.doc,.docx"
                      solo
                      dense
                      hide-details
                      style="max-width: 250px;"
                      color="#069"
                      @change="onFileSelected(candidate.id)"
                    ></v-file-input>
                  </td>
                  <td class="text-center">
                    <span
                      v-if="uploadStatus[candidate.id] === 'uploaded'"
                      class="badge badge-success"
                      >Uploaded</span
                    >
                    <span
                      v-else-if="uploadStatus[candidate.id] === 'uploading'"
                      class="badge badge-info"
                      >Uploading...</span
                    >
                    <span v-else class="text-muted">Not uploaded</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          text
          @click="handleCancel"
          :disabled="uploading"
        >
          Cancel
        </v-btn>
        <v-btn
          color="grey"
          text
          @click="handleSkip"
          :disabled="uploading"
        >
          Skip & Continue
        </v-btn>
        <v-btn
          color="success"
          dark
          @click="handleUploadAndContinue"
          :disabled="uploading"
        >
          <i class="fal fa-upload mr-2"></i>
          {{ uploading ? 'Uploading...' : 'Upload & Continue' }}
          <v-icon v-if="uploading" class="ml-2" style="font-size: 16px;">
            fal fa-circle-notch fa-spin
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { BASE_URL } from '@/main';

export default {
  name: 'OfferLetterUpload',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    candidates: {
      type: Array,
      default: () => [],
    },
    companyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      offerLetters: {}, // { candidateId: File }
      uploadStatus: {}, // { candidateId: 'uploaded' | 'uploading' | null }
      uploading: false,
    };
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
      if (!newVal) {
        this.reset();
      }
    },
    dialog(newVal) {
      this.$emit('input', newVal);
    },
  },
  methods: {
    onFileSelected(candidateId) {
      // Reset status when a new file is selected
      this.$set(this.uploadStatus, candidateId, null);
    },
    reset() {
      this.offerLetters = {};
      this.uploadStatus = {};
      this.uploading = false;
    },
    handleCancel() {
      this.$emit('cancel');
      this.dialog = false;
    },
    handleSkip() {
      this.$emit('skip');
      this.dialog = false;
    },
    async handleUploadAndContinue() {
      // Get all candidates with files
      const candidatesWithFiles = this.candidates.filter(
        (c) => this.offerLetters[c.id]
      );

      if (candidatesWithFiles.length === 0) {
        // No files selected, just continue
        this.$emit('continue');
        this.dialog = false;
        return;
      }

      this.uploading = true;

      // Store files temporarily - we'll upload them after onboarding
      const filesToUpload = candidatesWithFiles.map((candidate) => ({
        candidateId: candidate.id,
        candidateEmail: candidate.email,
        file: this.offerLetters[candidate.id],
      }));

      // Emit the files to parent component
      this.$emit('files-selected', filesToUpload);
      this.$emit('continue');
      this.dialog = false;
      this.uploading = false;
    },
    async uploadOfferLetters(filesToUpload, candidateToEmployeeMap) {
      // Upload offer letters after onboarding is complete
      // candidateToEmployeeMap: { candidateId: employeeId }
      const uploadPromises = filesToUpload.map(async (item) => {
        const employeeId = candidateToEmployeeMap[item.candidateId];
        if (!employeeId) {
          console.warn(
            `No employee ID found for candidate ${item.candidateId}`
          );
          return null;
        }

        this.$set(this.uploadStatus, item.candidateId, 'uploading');

        const formData = new FormData();
        formData.append('id', employeeId);
        formData.append('company_id', this.companyId);
        formData.append('file', item.file);

        try {
          await axios.post(`${BASE_URL}uploadofferletter`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            },
          });
          this.$set(this.uploadStatus, item.candidateId, 'uploaded');
          return { success: true, candidateId: item.candidateId };
        } catch (error) {
          console.error(
            `Error uploading offer letter for candidate ${item.candidateId}:`,
            error
          );
          this.$set(this.uploadStatus, item.candidateId, 'error');
          return { success: false, candidateId: item.candidateId, error };
        }
      });

      const results = await Promise.all(uploadPromises);
      return results.filter((r) => r !== null);
    },
  },
};
</script>

<style scoped>
.badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}
</style>

